/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Per-route JSON-LD payload builders — the structured data that used to exist
 * ONLY inside a client `useEffect`.
 *
 * Why this module exists: AI answer engines do not run JavaScript. Every node
 * built here was, until 2026-09-01, appended to `document.head` after
 * hydration, which means the crawlers the whole GEO effort targets saw a head
 * with no structured data at all — most painfully on the homepage, whose
 * Dataset node exists specifically to point agents at /api/providers.
 *
 * Plain ESM JavaScript on purpose (the deepMerge.js / faqSchema.js pattern)
 * because it has consumers that cannot share TypeScript:
 *   1. the client hooks under src/hooks/ — the source of truth at runtime:
 *      they replace the baked script in place after hydration (same id)
 *   2. the head of every page (src/lib/seoHead.js through
 *      src/lib/routeHeadSchema.js) — the bake and the request-time head,
 *      the server build reaching it via tsconfig.server.json's allowJs
 *
 * Every builder is PURE: no `window`, no `document`, no fs, no i18n import.
 * Text comes in through a `lookup(key)` callback — `t()` on the client,
 * `tLookup(translations, key)` on the server — so a single builder serves all
 * three call sites and the baked bytes equal the hydrated bytes.
 */

import { AUTHOR_SEGMENT } from './authorSlug.js';
import { BEGRIPPEN_SEGMENT, begripPath } from './begrippen.js';
import { providerProfilePath } from './providerRoutes.js';
import { compareNewestFirst } from './kennisbankIndex.js';
import { lowestTierPct, rowOfferPricing } from './offerPricing.js';

const DEFAULT_HOST = 'https://ere-vergelijk.nl';

/* -------------------------------------------------------------------------
 * Script ids. These MUST match the ids the client hooks manage: the hook
 * looks the node up by id and rewrites its textContent, so a mismatch does
 * not "fail" — it silently ships two competing copies of the same schema.
 * ---------------------------------------------------------------------- */

/** Homepage ItemList + Dataset graph — src/hooks/useProviderListSchema.ts. */
export const PROVIDER_LIST_JSONLD_ID = 'jsonld-providerlist';
/** Glossary DefinedTermSet — src/hooks/useDefinedTermSchema.ts. */
export const DEFINED_TERM_JSONLD_ID = 'jsonld-definedtermset';
/** MID-check HowTo — the inline effect in src/pages/MidCheckPage.tsx. */
export const MID_CHECK_HOWTO_JSONLD_ID = 'jsonld-mid-check-howto';
/** Kennisbank hub CollectionPage — bake-only, no client twin. */
export const COLLECTION_JSONLD_ID = 'jsonld-collectionpage';
/** NEa-overview ItemList — bake-only, no client twin. */
export const NEA_LIST_JSONLD_ID = 'jsonld-nea-itemlist';
/** /ere-prijs Dataset — the inline effect in src/pages/ErePricePage.tsx. */
export const ERE_PRICE_DATASET_JSONLD_ID = 'jsonld-ere-price-dataset';

/**
 * Missing-translation guard. `t()` and `tLookup()` both answer a missing key
 * with the key path itself, which is a string and therefore passes every naive
 * truthiness check — that is how `name: "mid_checker.diy.step_2_title"`
 * reaches a crawler.
 * @param {unknown} value
 * @param {string} key
 * @returns {boolean}
 */
const hasValue = (value, key) => typeof value === 'string' && value.trim() !== '' && value !== key;

/** @param {'nl'|'en'} lang */
const inLanguage = (lang) => (lang === 'nl' ? 'nl-NL' : 'en-US');

/**
 * @param {string|null|undefined} url
 * @param {string} host
 * @returns {string|undefined}
 */
const absolute = (url, host) => {
  if (!url) return undefined;
  if (url.startsWith('http')) return url;
  return `${host}${url.startsWith('/') ? '' : '/'}${url}`;
};

/* =========================================================================
 * Homepage — ItemList + Service + Offer + Dataset
 * ====================================================================== */

/**
 * The words an Offer's description names its price by: a flat row's
 * `commissionFee` as always ("15% commissie"); a staffel "vanaf {lowest}%
 * commissie (staffel)" — its lowest band, the overview's "vanaf"; a vastrecht
 * offer "{pct}% commissie + vastrecht per kWh". The PercentageFeePrice stays the
 * row's `commissionFee` (for a volume-dependent offer the nominal % at its
 * audience's reference volume — the figure the site prints for it).
 *
 * @param {any} p — a flattened /api/providers row
 * @returns {string}
 */
function offerTermsOf(p) {
  const pricing = p.pricing ? rowOfferPricing(p) : null;
  if (pricing?.model === 'tiered') {
    return `vanaf ${lowestTierPct(pricing.tiers)}% commissie (staffel)`;
  }
  if (pricing?.model === 'commission_plus_kwh_fee') {
    return pricing.eurPerKwh === null
      ? `${pricing.commissionPct}% commissie met een minimum/maximum per jaar`
      : `${pricing.commissionPct}% commissie + vastrecht per kWh`;
  }
  return `${p.commissionFee}% commissie`;
}

/**
 * The comparison graph for the homepage: every visible provider as a Service
 * (with an Offer carrying the commission as a PercentageFeePrice; no
 * AggregateRating since the ratings left, 2026-10-14), plus the Dataset node that
 * points LLM/agent crawlers at the machine-readable /api/providers feed — the
 * contract documented in public/llms.txt.
 *
 * MIRRORED BY src/hooks/useProviderListSchema.ts, which imports this builder.
 *
 * @param {any[]} providers — the flattened /api/providers rows
 * @param {string} [host]
 * @returns {object|null} null when there is nothing to list
 */
export function buildProviderListJsonLd(providers, host = DEFAULT_HOST) {
  if (!Array.isArray(providers) || providers.length === 0) return null;

  const itemList = {
    '@type': 'ItemList',
    name: 'ERE inboekdienstverleners Nederland',
    numberOfItems: providers.length,
    itemListElement: providers.map((p, i) => {
      /** @type {Record<string, unknown>} */
      const service = {
        '@type': 'Service',
        name: p.name,
        serviceType: 'ERE inboekdienst',
        areaServed: 'NL',
        provider: {
          '@type': 'Organization',
          name: p.name,
          ...(absolute(p.logoUrl, host) ? { logo: absolute(p.logoUrl, host) } : {}),
          ...(p.signupUrl ? { url: p.signupUrl } : {}),
        },
      };

      if (p.commissionFee !== null && p.commissionFee !== undefined) {
        service.offers = {
          '@type': 'Offer',
          description: `${offerTermsOf(p)}${p.contractDuration ? ` · ${p.contractDuration}` : ''}`,
          category: p.targetAudience,
          priceCurrency: 'EUR',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            priceType: 'https://schema.org/PercentageFeePrice',
            price: p.commissionFee,
          },
        };
      }

      return { '@type': 'ListItem', position: i + 1, item: service };
    }),
  };

  const dataset = {
    '@type': 'Dataset',
    name: 'ERE-inboekdienstverleners — vergelijking Nederland',
    // What the comparison really ranks on: no review scores (public/llms.txt
    // says so to agents), and not every row is on the NEa list (Tap Electric
    // resells through a registered party).
    description:
      'Actuele vergelijking van ERE-inboekdienstverleners in Nederland met een aanbod voor laadpaaleigenaren, op commissie, servicekosten, uitbetaling en looptijd.',
    creator: { '@type': 'Organization', name: 'ERE-Vergelijk', url: host },
    isAccessibleForFree: true,
    license: 'https://creativecommons.org/licenses/by/4.0/',
    inLanguage: ['nl', 'en'],
    keywords: ['ERE', 'Emissiereductie-eenheden', 'laadpaal', 'inboekdienstverlener', 'NEa'],
    distribution: {
      '@type': 'DataDownload',
      encodingFormat: 'application/json',
      contentUrl: `${host}/api/providers`,
    },
  };

  return { '@context': 'https://schema.org', '@graph': [itemList, dataset] };
}

/* =========================================================================
 * /ere-prijs — Dataset
 * ====================================================================== */

/**
 * The price index as a Dataset: a dated series with its distribution, so an
 * answer engine can lift "what is an ERE worth today" WITH the date and the
 * publisher attached instead of scraping a number off a card.
 *
 * `variableMeasured` carries the reading in force as a PropertyValue — the
 * one field a model actually quotes — while `temporalCoverage` and the JSON
 * distribution say where the rest of the series lives. Returns null for an
 * empty series: a Dataset node describing no data is worse than none.
 *
 * @param {Array<{date: string, price: number, low: number, high: number}>} entries oldest first
 * @param {{lang: 'nl'|'en', name?: string, description?: string, host?: string}} opts
 * @returns {object|null}
 */
export function buildErePriceDatasetJsonLd(entries, opts) {
  const list = Array.isArray(entries) ? entries : [];
  if (list.length === 0) return null;
  const host = opts?.host ?? DEFAULT_HOST;
  const lang = opts?.lang === 'en' ? 'en' : 'nl';
  const current = list[list.length - 1];
  const first = list[0];
  const name = hasValue(opts?.name, 'seo.ere_price_title')
    ? opts.name
    : 'ERE-prijs — actuele marktwaarde en historiek';
  const description = hasValue(opts?.description, 'seo.ere_price_description')
    ? opts.description
    : 'Gedateerde reeks van de marktwaarde van één ERE in Nederland, met bandbreedte en bron per meting.';
  const pageUrl = `${host}/${lang}/ere-prijs`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    '@id': `${pageUrl}#dataset`,
    name,
    description,
    url: pageUrl,
    inLanguage: inLanguage(lang),
    isAccessibleForFree: true,
    license: 'https://creativecommons.org/licenses/by/4.0/',
    creator: { '@type': 'Organization', name: 'ERE-Vergelijk', url: host },
    keywords: ['ERE', 'emissiereductie-eenheid', 'ERE-prijs', 'HBE', 'laadpaal', 'marktwaarde'],
    temporalCoverage: `${first.date}/${current.date}`,
    dateModified: current.date,
    measurementTechnique:
      lang === 'en'
        ? 'Midpoint of the publicly quoted market range for one ERE, recorded per observation date with its source.'
        : 'Middenwaarde van de publiek genoteerde marktbandbreedte voor één ERE, per peildatum vastgelegd met bron.',
    variableMeasured: [
      {
        '@type': 'PropertyValue',
        name: lang === 'en' ? 'ERE market value' : 'Marktwaarde per ERE',
        value: current.price,
        minValue: current.low,
        maxValue: current.high,
        unitText: 'EUR/ERE',
        observationDate: current.date,
      },
    ],
    distribution: {
      '@type': 'DataDownload',
      encodingFormat: 'application/json',
      contentUrl: `${host}/api/ere-price`,
    },
  };
}

/* =========================================================================
 * Page-specific FAQ entry derivations
 * ====================================================================== */

/**
 * /geen-laadpaal's pairs. 2_v5 points at a no-obligation quote through the
 * aanvraagformulier and 4_v4 answers "Zit ik ergens aan vast?" for that form
 * (we handle the request and may pass it to Laadpaaldirect); neither calls
 * them "onze partner". 2_v5 replaced 2_v4 on 2026-10-07, when the hub took the
 * form over and the province picker went: 2_v4 still said "Kies je provincie
 * en …". Until 2026-10-07 a build without the installer partner (the
 * waitlist) read 2_v2 and 4 instead; that state is gone. 2_v2, 4, 2_v3, 4_v2
 * and 4_v3 (the click-out pairs of 2026-10-02) and 2_v4 left the baselines in
 * the i18n sweep of 2026-10-14; their values stay frozen in i18n_admin on acc
 * and prd, read by nothing. Since the redesign of 2026-10-21 three more:
 * 5_v1 (1-fase or 3-fase, and what each delivers), 6_v1 (does it have to be a
 * smart charge point — many ERE providers read sessions automatically) and
 * 7_v1 (can a current charge point get a MID meter — for a few models, check
 * yours in the MID checker; no retrofit is named as a fact). One list for the
 * page, the baked FAQPage and the request-time head.
 */
const GEEN_LAADPAAL_FAQ_PAIRS = ['1', '2_v5', '3', '4_v4', '5_v1', '6_v1', '7_v1'];

/**
 * Which `<ns>.faq_q<N>` / `<ns>.faq_a<N>` pairs each FAQ-carrying page renders.
 *
 * MIRRORS the `React.useMemo` in the pages themselves (ZakelijkPage,
 * HowWeComparePage, GeenLaadpaalPage). They hand the result to `useFAQSchema`,
 * which builds the node with the SAME shared builder the bake uses
 * (buildFAQPageJsonLd in src/lib/faqSchema.js) under the SAME id — so the only
 * thing that could differ between the baked head and the hydrated one is this
 * table. Change a page's range, change it here in the same commit.
 *
 * `skipMissing` mirrors HowWeComparePage's `hasValue` filter (its FAQ block is
 * CMS-editable and may be left partly empty); the other two pages ship a fixed
 * set of baseline keys and pass them through unfiltered.
 *
 * `pairs` replaces `count` when a pair had to move to a new key. A reworded
 * baseline key does not reach an environment whose CMS copy has frozen the old
 * value (docs/i18n-masking.md), so a corrected answer ships under a new id.
 * GeenLaadpaalPage calls pageFaqEntries itself rather than keeping a copy of
 * this list.
 *
 * @type {Record<string, {ns: string, count?: number, pairs?: string[], skipMissing: boolean}>}
 */
export const PAGE_FAQ_SOURCES = {
  zakelijk: { ns: 'zakelijk', count: 5, skipMissing: false },
  'hoe-wij-vergelijken': { ns: 'how_we_compare', count: 5, skipMissing: true },
  'geen-laadpaal': { ns: 'geen_laadpaal', pairs: GEEN_LAADPAAL_FAQ_PAIRS, skipMissing: false },
};

/**
 * The pair ids a source renders, in order: its explicit `pairs`, else 1…count.
 *
 * @param {{count?: number, pairs?: string[]}} source
 * @returns {string[]}
 */
export function pageFaqPairIds(source) {
  if (Array.isArray(source.pairs)) return source.pairs;
  return Array.from({ length: source.count ?? 0 }, (_, i) => String(i + 1));
}

/**
 * Resolve one page's FAQ pairs into the `{question, answer}` entries
 * buildFAQPageJsonLd takes. Returns `[]` for a segment with no page FAQ.
 *
 * @param {string} segment — route segment without a leading slash
 * @param {(key: string) => string} lookup
 * @returns {Array<{question: string, answer: string}>}
 */
export function pageFaqEntries(segment, lookup) {
  const source = PAGE_FAQ_SOURCES[segment];
  if (!source) return [];
  const entries = [];
  for (const id of pageFaqPairIds(source)) {
    const qKey = `${source.ns}.faq_q${id}`;
    const aKey = `${source.ns}.faq_a${id}`;
    const question = lookup(qKey);
    const answer = lookup(aKey);
    if (source.skipMissing && !(hasValue(question, qKey) && hasValue(answer, aKey))) continue;
    entries.push({ question, answer });
  }
  return entries;
}

/* =========================================================================
 * /begrippen — DefinedTermSet, and /begrippen/:slug — DefinedTerm
 * ====================================================================== */

/**
 * Term/definition pairs for the glossary's DefinedTermSet, each with the URL
 * of the page that term now has.
 *
 * Takes the list rows both payload mirrors build with `begripIndexRows`
 * (src/lib/begrippen.js) — the rows the page renders — so the set lists
 * exactly the cards on screen. Until 2026-09-30 the pairs were read from 32
 * numbered translation keys and the count lived in two constants a test had to
 * keep equal; the terms are records now, and there is no count.
 *
 * A row without a term or a definition is skipped: an empty `name` in a
 * DefinedTermSet is invisible on screen and quotable by an answer engine.
 *
 * @param {Array<{slug?: string, term?: string, definition?: string}> | null | undefined} rows
 * @param {'nl'|'en'} lang
 * @param {string} [host]
 * @returns {Array<{term: string, definition: string, url?: string}>}
 */
export function glossarySchemaTerms(rows, lang, host = DEFAULT_HOST) {
  const terms = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const term = typeof row?.term === 'string' ? row.term.trim() : '';
    const definition = typeof row?.definition === 'string' ? row.definition.trim() : '';
    if (!term || !definition) continue;
    const slug = typeof row?.slug === 'string' ? row.slug.trim() : '';
    terms.push({
      term,
      definition,
      ...(slug ? { url: `${host}${begripPath(lang, slug)}` } : {}),
    });
  }
  return terms;
}

/** i18n key holding the glossary's own title, and what to use when it is missing. */
export const GLOSSARY_TITLE_KEY = 'begrippen.title';
export const GLOSSARY_FALLBACK_NAME = 'Begrippenlijst';

/**
 * The name to publish for the DefinedTermSet.
 *
 * Applied INSIDE the builder rather than by each caller, because the two
 * callers were not applying it equally: the bake (routeHeadSchema.js) mapped a
 * missing `begrippen.title` onto the fallback, while the client hook handed
 * `t('begrippen.title')` straight through — so on a missing key the baked head
 * said "Begrippenlijst" and the hydrated one replaced it with the literal
 * string `begrippen.title` as the name of the glossary. Normalising here makes
 * the two identical no matter what a caller passes.
 *
 * @param {unknown} name
 * @returns {string}
 */
export function glossarySetName(name) {
  return hasValue(name, GLOSSARY_TITLE_KEY) ? /** @type {string} */ (name) : GLOSSARY_FALLBACK_NAME;
}

/**
 * DefinedTermSet payload — clean term→definition pairs for answer engines to
 * extract, and a structured glossary for search engines.
 *
 * MIRRORED BY src/hooks/useDefinedTermSchema.ts, which imports this builder.
 *
 * @param {string} name — the glossary's own title
 * @param {string} url — canonical URL of the glossary page
 * @param {Array<{term: string, definition: string, url?: string}>} terms — see {@link glossarySchemaTerms}
 * @returns {object|null} null when no complete pair survives
 */
export function buildDefinedTermSetJsonLd(name, url, terms) {
  const valid = (terms ?? []).filter((t) => t && t.term && t.definition);
  if (valid.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: glossarySetName(name),
    url,
    hasDefinedTerm: valid.map((t) => ({
      '@type': 'DefinedTerm',
      name: t.term,
      description: t.definition,
      inDefinedTermSet: url,
      // The term's own page — where an engine that wants more than the one
      // sentence goes next.
      ...(t.url ? { url: t.url } : {}),
    })),
  };
}

/** One term page's DefinedTerm — src/pages/BegripPage.tsx manages it after hydration. */
export const DEFINED_TERM_PAGE_JSONLD_ID = 'jsonld-definedterm';
/**
 * FAQPage built from the FAQ blocks in a term page's explanation. The id an
 * article's FAQ blocks use (buildArticleHeadExtras in src/lib/articleSeo.js) —
 * same builder, same block schema, and an article and a term page are never
 * one document.
 */
export const BEGRIP_FAQ_JSONLD_ID = 'jsonld-faq';

/**
 * The DefinedTerm node of one term page: the term, its definition, its own URL
 * and the set it belongs to. The set is referenced by the glossary's URL — the
 * same value every entry of the list's DefinedTermSet carries — so an engine
 * can tie the page to the list without either repeating the other.
 *
 * `contentLang` is the language of the WORDS (src/lib/begrippen.js
 * begripContentLanguage), which on an untranslated begrip's /en/ page is
 * Dutch; `lang` is the URL's and only builds the links.
 *
 * MIRRORED BY src/pages/BegripPage.tsx, which calls this builder with the same
 * detail payload the bake used.
 *
 * @param {{slug?: string, term?: string, definition?: string} | null | undefined} detail
 * @param {{lang: 'nl'|'en', contentLang?: 'nl'|'en', setName?: string, host?: string}} opts
 * @returns {object|null} null without a term, a definition or a slug
 */
export function buildDefinedTermJsonLd(detail, opts) {
  const term = typeof detail?.term === 'string' ? detail.term.trim() : '';
  const definition = typeof detail?.definition === 'string' ? detail.definition.trim() : '';
  const slug = typeof detail?.slug === 'string' ? detail.slug.trim() : '';
  if (!term || !definition || !slug) return null;
  const lang = opts?.lang === 'en' ? 'en' : 'nl';
  const host = opts?.host ?? DEFAULT_HOST;
  const url = `${host}${begripPath(lang, slug)}`;
  const setUrl = `${host}/${lang}/${BEGRIPPEN_SEGMENT}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    '@id': `${url}#term`,
    name: term,
    description: definition,
    url,
    inLanguage: inLanguage(
      opts?.contentLang === 'en' || opts?.contentLang === 'nl' ? opts.contentLang : lang
    ),
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: glossarySetName(opts?.setName),
      url: setUrl,
    },
  };
}

/* =========================================================================
 * /mid-check — HowTo
 * ====================================================================== */

/** How many DIY steps the MID wizard presents (mid_checker.diy.step_N_*). */
export const MID_CHECK_STEP_COUNT = 4;

/** The HowTo's own title. Without it there is no node — see the builder. */
export const MID_CHECK_HOWTO_NAME_KEY = 'mid_check.howto_name';
/** Optional lead-in. Omitted rather than published as a key path. */
export const MID_CHECK_HOWTO_DESCRIPTION_KEY = 'mid_checker.diy.intro';

/**
 * The four DIY steps the MID wizard already shows, as a HowTo so search
 * engines can surface them without the visitor reaching the diy step.
 *
 * USED BY src/pages/MidCheckPage.tsx as well — it imports this builder rather
 * than keeping a copy, so its in-place rewrite after hydration is a no-op. The
 * copy it used to keep had already drifted: a missing step translation made
 * the bake drop the step and made the client publish the raw key path as the
 * step name.
 *
 * The HowTo's OWN name and description run through the same `hasValue` gate as
 * every step, and for the same reason: `t()`/`tLookup()` answer a missing key
 * with the key path, which is a non-empty string, so an unguarded assignment
 * bakes `name: "mid_check.howto_name"` into both mirrors — invisible on screen
 * and quotable by an answer engine. A HowTo with no title is not worth
 * publishing at all (`name` is what a rich result shows), so a missing title
 * drops the whole node; the description is optional in schema.org terms and is
 * simply left out.
 *
 * @param {(key: string) => string} lookup
 * @returns {object|null} null when no step resolves, or when the title does not
 */
export function buildMidCheckHowToJsonLd(lookup) {
  const steps = [];
  for (let n = 1; n <= MID_CHECK_STEP_COUNT; n++) {
    const titleKey = `mid_checker.diy.step_${n}_title`;
    const bodyKey = `mid_checker.diy.step_${n}_body`;
    const name = lookup(titleKey);
    const text = lookup(bodyKey);
    if (!hasValue(name, titleKey) || !hasValue(text, bodyKey)) continue;
    steps.push({ '@type': 'HowToStep', position: steps.length + 1, name, text });
  }
  if (steps.length === 0) return null;

  const name = lookup(MID_CHECK_HOWTO_NAME_KEY);
  if (!hasValue(name, MID_CHECK_HOWTO_NAME_KEY)) return null;
  const description = lookup(MID_CHECK_HOWTO_DESCRIPTION_KEY);

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    ...(hasValue(description, MID_CHECK_HOWTO_DESCRIPTION_KEY) ? { description } : {}),
    totalTime: 'PT5M',
    step: steps,
  };
}

/* =========================================================================
 * /kennisbank — CollectionPage
 * ====================================================================== */

/**
 * The kennisbank hub as a CollectionPage wrapping an ItemList of its articles.
 * Bake-only: the hub has no client JSON-LD twin, so this node is the only
 * structured description a crawler gets of what the knowledge base contains.
 *
 * Takes the RAW article records plus resolver callbacks so the `published`
 * filter, the sort and the empty-title skip live in ONE place — both mirrors
 * already own `effectiveSlug` / `effectiveArticleTranslation` and just bind
 * them in. The sort is the listing's own (newest first,
 * src/lib/kennisbankIndex.js), so ListItem positions match the page.
 *
 * Untranslated articles stay in the English list: the hub page renders their
 * cards too (marked Dutch-only), and dropping them would hide half the
 * knowledge base from English readers. Their own /en/ URL carries the
 * noindex,follow stub gate — that is where the signal belongs.
 *
 * @param {{articles: any[], lang: 'nl'|'en', name: string, description?: string,
 *   slugOf: (article: any, lang: 'nl'|'en') => string,
 *   titleOf: (article: any, lang: 'nl'|'en') => string, host?: string}} opts
 * @returns {object|null} null when nothing is published
 */
export function buildKennisbankCollectionJsonLd(opts) {
  const { articles, lang, name, description, slugOf, titleOf } = opts;
  const host = opts.host ?? DEFAULT_HOST;
  const url = `${host}/${lang}/kennisbank`;

  const published = (Array.isArray(articles) ? articles : [])
    .filter((a) => a?.published === true)
    // Copy-free: `filter` already returned a new array, so sorting it in place
    // cannot disturb the caller's list.
    .sort(compareNewestFirst);

  const itemListElement = [];
  for (const article of published) {
    const slug = slugOf(article, lang);
    const title = titleOf(article, lang);
    if (!slug || !title) continue;
    itemListElement.push({
      '@type': 'ListItem',
      position: itemListElement.length + 1,
      name: title,
      url: `${host}/${lang}/kennisbank/${slug}`,
    });
  }
  if (itemListElement.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    ...(description ? { description } : {}),
    url,
    inLanguage: inLanguage(lang),
    isPartOf: { '@type': 'WebSite', name: 'ERE-Vergelijk', url: host },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: itemListElement.length,
      itemListElement,
    },
  };
}

/* =========================================================================
 * /providers — ItemList
 * ====================================================================== */

/**
 * The NEa-registered aanbieders overview as an ItemList. Bake-only (no client
 * twin): this is the machine-readable form of the page's single job — "who is
 * registered with the NEa" — and the list a crawler otherwise has to infer
 * from anchor text.
 *
 * Each entry points at the on-site profile page, matching the link the page
 * actually renders (NEAProvidersPage links every row with a slug). A provider
 * without a slug carries its name only.
 *
 * @param {Array<{name?: string, slug?: string}>} providers
 * @param {'nl'|'en'} lang
 * @param {string} name — localized list title
 * @param {string} [host]
 * @returns {object|null} null when there is nothing to list
 */
export function buildNeaProviderListJsonLd(providers, lang, name, host = DEFAULT_HOST) {
  const itemListElement = [];
  for (const provider of Array.isArray(providers) ? providers : []) {
    const providerName = typeof provider?.name === 'string' ? provider.name.trim() : '';
    if (!providerName) continue;
    const slug = typeof provider?.slug === 'string' ? provider.slug.trim() : '';
    itemListElement.push({
      '@type': 'ListItem',
      position: itemListElement.length + 1,
      name: providerName,
      ...(slug ? { url: `${host}${providerProfilePath(lang, slug)}` } : {}),
    });
  }
  if (itemListElement.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    inLanguage: inLanguage(lang),
    numberOfItems: itemListElement.length,
    itemListElement,
  };
}

/* =========================================================================
 * /auteur/:slug — Person
 *
 * The author page's own node. Article pages already reference their author
 * inside the Article node; this is the page an engine lands on when it
 * follows that reference, so it carries the full profile: job title, bio,
 * portrait and the sameAs links that let an engine tie the name to the
 * LinkedIn/X/website identities (E-E-A-T is exactly this linkage).
 * ========================================================================= */
export const AUTHOR_PERSON_JSONLD_ID = 'jsonld-author-person';

/**
 * @param {any} author  The public author shape (`toPublicAuthor` on the
 *   server, the prerender's copy, or the /api/authors payload).
 * @param {{ lang: 'nl'|'en', host?: string }} opts
 * @returns {object | null} null when there is no name or slug — an anonymous
 *   Person node would be noise, not data.
 */
export function buildAuthorPersonJsonLd(author, opts) {
  const lang = opts?.lang === 'en' ? 'en' : 'nl';
  const host = opts?.host ?? DEFAULT_HOST;
  const name = typeof author?.displayName === 'string' ? author.displayName.trim() : '';
  const slug = typeof author?.slug === 'string' ? author.slug.trim() : '';
  if (!name || !slug) return null;

  const url = `${host}/${lang}/${AUTHOR_SEGMENT}/${slug}`;
  const jobTitle = typeof author?.title === 'string' ? author.title.trim() : '';
  const bio =
    (typeof author?.bio?.[lang] === 'string' && author.bio[lang].trim()) ||
    (typeof author?.bio?.nl === 'string' && author.bio.nl.trim()) ||
    '';
  const avatar = typeof author?.avatarPath === 'string' ? author.avatarPath.trim() : '';
  const image = !avatar
    ? ''
    : /^https?:\/\//i.test(avatar)
      ? avatar
      : `${host}${avatar.startsWith('/') ? '' : '/'}${avatar}`;
  const sameAs = [author?.linkedinUrl, author?.twitterUrl, author?.websiteUrl]
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter((s) => /^https?:\/\//i.test(s));

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${url}#person`,
    name,
    url,
    ...(jobTitle ? { jobTitle } : {}),
    ...(bio ? { description: bio } : {}),
    ...(image ? { image } : {}),
    ...(sameAs.length ? { sameAs } : {}),
    worksFor: { '@type': 'Organization', name: 'ERE-Vergelijk', url: `${host}/${lang}` },
  };
}

/* =========================================================================
 * /laadpalen — the brand hub, and /laadpalen/:slug — one brand's models
 *
 * Both take the payloads GET /api/charger-brands[/:slug] serves (built by
 * src/lib/chargerCatalogRecords.js chargerBrandIndexRows / chargerBrandDetailPayload,
 * the functions the bake seeds the pages with) and read their names through
 * `lookup`, so the baked head and the hydrated one are the same bytes.
 * MIRRORED BY src/pages/ChargerBrandsPage.tsx and ChargerBrandPage.tsx.
 * ====================================================================== */

/** The hub's ItemList — src/pages/ChargerBrandsPage.tsx manages it after hydration. */
export const CHARGER_BRANDS_JSONLD_ID = 'jsonld-charger-brands';
/** A brand page's ItemList — src/pages/ChargerBrandPage.tsx manages it after hydration. */
export const CHARGER_BRAND_JSONLD_ID = 'jsonld-charger-brand';

/** The hub's own title, which names its list; without it there is no node. */
export const CHARGER_BRANDS_LIST_NAME_KEY = 'charger_brands.hero_title';
/** `{brand}` template naming a brand page's list; without it the brand's name does. */
export const CHARGER_BRAND_LIST_NAME_KEY = 'charger_brand.models_list_name';

/**
 * The URL segment — CHARGER_BRANDS_SEGMENT in src/lib/chargerCatalog.js,
 * spelled here once more because this block may not add an import; pinned
 * equal in src/__tests__/chargerBrandPages.test.tsx.
 */
const CHARGER_BRANDS_PATH_SEGMENT = 'laadpalen';

/** @param {unknown} value */
const trimmedText = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * The brand hub as an ItemList of its brand pages, in the hub's own order.
 * Rows without a name or a slug are skipped; nothing to list → null.
 *
 * @param {Array<{slug?: string, brand?: string}> | null | undefined} rows  the hub rows
 * @param {{lang: 'nl'|'en', lookup: (key: string) => string, host?: string}} opts
 * @returns {object|null}
 */
export function buildChargerBrandsHubJsonLd(rows, opts) {
  const lang = opts?.lang === 'en' ? 'en' : 'nl';
  const host = opts?.host ?? DEFAULT_HOST;
  const name = typeof opts?.lookup === 'function' ? opts.lookup(CHARGER_BRANDS_LIST_NAME_KEY) : '';
  if (!hasValue(name, CHARGER_BRANDS_LIST_NAME_KEY)) return null;
  const url = `${host}/${lang}/${CHARGER_BRANDS_PATH_SEGMENT}`;
  const itemListElement = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const brand = trimmedText(row?.brand);
    const slug = trimmedText(row?.slug);
    if (!brand || !slug) continue;
    itemListElement.push({
      '@type': 'ListItem',
      position: itemListElement.length + 1,
      name: brand,
      url: `${url}/${slug}`,
    });
  }
  if (itemListElement.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${url}#brands`,
    name: name.trim(),
    url,
    inLanguage: inLanguage(lang),
    numberOfItems: itemListElement.length,
    itemListElement,
  };
}

/**
 * One brand page as an ItemList of the brand's models, each at its anchor on
 * the page (`…/laadpalen/alfen#eve-single-pro-line`), `about` the Brand — with
 * its website and logo where those are known, never an empty field. No
 * Product nodes: without prices or reviews (there are none, by decision) a
 * Product is a rich-result warning, not data.
 *
 * The anchor is the payload's `anchor`, AS GIVEN: chargerBrandDetailPayload
 * (src/lib/chargerCatalogRecords.js) hands out one per model that is unique on the
 * page and never empty, and the page puts exactly that id on the model — so
 * every URL here lands on its model, two models that fold alike included.
 * Never re-derive or de-duplicate it here.
 *
 * @param {{brand?: {slug?: string, brand?: string, website?: string, logo?: string},
 *   models?: Array<{model?: string, anchor?: string}>} | null | undefined} detail  the brand page payload
 * @param {{lang: 'nl'|'en', lookup: (key: string) => string, host?: string}} opts
 * @returns {object|null} null without a brand name, a slug or a model
 */
export function buildChargerBrandJsonLd(detail, opts) {
  const brand = trimmedText(detail?.brand?.brand);
  const slug = trimmedText(detail?.brand?.slug);
  if (!brand || !slug) return null;
  const lang = opts?.lang === 'en' ? 'en' : 'nl';
  const host = opts?.host ?? DEFAULT_HOST;
  const pageUrl = `${host}/${lang}/${CHARGER_BRANDS_PATH_SEGMENT}/${slug}`;
  const itemListElement = [];
  for (const row of Array.isArray(detail?.models) ? detail.models : []) {
    const model = trimmedText(row?.model);
    const anchor = typeof row?.anchor === 'string' ? row.anchor : '';
    if (!model) continue;
    itemListElement.push({
      '@type': 'ListItem',
      position: itemListElement.length + 1,
      name: `${brand} ${model}`,
      url: anchor ? `${pageUrl}#${anchor}` : pageUrl,
    });
  }
  if (itemListElement.length === 0) return null;
  const template =
    typeof opts?.lookup === 'function' ? opts.lookup(CHARGER_BRAND_LIST_NAME_KEY) : '';
  // A replacer function: a `$'` or `$&` in a brand name is text, not a pattern.
  const name = hasValue(template, CHARGER_BRAND_LIST_NAME_KEY)
    ? template.replace(/\{brand\}/g, () => brand).trim()
    : brand;
  const website = trimmedText(detail?.brand?.website);
  const logo = absolute(trimmedText(detail?.brand?.logo), host);
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${pageUrl}#models`,
    name,
    url: pageUrl,
    inLanguage: inLanguage(lang),
    numberOfItems: itemListElement.length,
    itemListElement,
    about: {
      '@type': 'Brand',
      name: brand,
      ...(/^https?:\/\//i.test(website) ? { url: website } : {}),
      ...(logo ? { logo } : {}),
    },
  };
}
